import { useEffect, useState } from "react";

export default function AdminLeafletUpload() {
  const [fileObj, setFileObj] = useState(null);
  const [filePath, setFilePath] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");

  // ----------------------------
  // LOAD META
  // ----------------------------
  async function loadMeta() {
    try {
      const res = await fetch("/api/leaflet-admin/meta", {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      console.log("DEBUG meta response:", data);

      if (data?.ok) setMeta(data.meta);
    } catch (e) {
      console.error("loadMeta error", e);
    }
  }

  // ----------------------------
  // LOAD HISTORY
  // ----------------------------
  async function loadHistory() {
    try {
      const res = await fetch("/api/leaflet-admin/list", {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      console.log("DEBUG history response:", data);

      if (data?.ok) setHistory(data.items || []);
    } catch (e) {
      console.error("loadHistory error", e);
    }
  }

  useEffect(() => {
    loadMeta();
    loadHistory();
  }, []);

  // ----------------------------
  // SUBMIT
  // ----------------------------
  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (!validFrom || !validTo) {
      setMsg("Vyplň prosím platnost (od/do).");
      return;
    }
    if (!fileObj && !filePath) {
      setMsg("Vyber soubor nebo zadej serverový filePath.");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      if (fileObj) fd.append("file", fileObj);
      if (filePath) fd.append("filePath", filePath);
      fd.append("validFrom", validFrom);
      fd.append("validTo", validTo);

      const res = await fetch("/api/leaflet-admin/upload", {
        method: "POST",
        credentials: "include",
        body: fd,
      });

      const data = await res.json();

      if (data?.ok) {
        setMsg("Leták uložen do databáze.");
        setMeta(data.meta);
        loadHistory();
        setFileObj(null);
        setFilePath("");
      } else {
        setMsg(data?.error || "Chyba při nahrávání.");
      }
    } catch {
      setMsg("Chyba sítě.");
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------
  // RENDER
  // ----------------------------
  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-4">Nahrát akční leták (LEMON)</h1>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium">Vybrat soubor (XLS/XLSX)</label>
          <input
            type="file"
            accept=".xls,.xlsx"
            className="mt-1"
            onChange={(e) => setFileObj(e.target.files?.[0] || null)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Zpracuje se soubor a data se uloží do databáze.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500">
            NEBO zadej serverový filePath (volitelné)
          </label>
          <input
            type="text"
            className="mt-1 w-full border rounded px-3 py-2"
            placeholder="C:\\Users\\...\\SEZNAM LETÁK.xlsx"
            value={filePath}
            onChange={(e) => setFilePath(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Platnost od</label>
            <input
              type="date"
              className="mt-1 w-full border rounded px-3 py-2"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Platnost do</label>
            <input
              type="date"
              className="mt-1 w-full border rounded px-3 py-2"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? "Nahrávám..." : "Nahrát leták"}
        </button>

        {msg && <div className="text-sm mt-2">{msg}</div>}
      </form>

      <hr className="my-6" />

      <div className="mb-6">
        <h2 className="text-lg font-medium mb-2">Aktuální metadata</h2>
        {meta ? (
          <div className="text-sm">
            <div>
              <b>Platnost:</b> {meta.validFrom} → {meta.validTo}
            </div>
            <div>
              <b>Zdroj:</b> {meta.source}
            </div>
            <div>
              <b>Uloženo:</b> {meta.savedAt}
            </div>
            <div>
              <b>Počet záznamů:</b> {meta.rows}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Zatím žádná metadata.</div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-medium mb-2">Historie posledních uploadů</h2>
        {history.length > 0 ? (
          <table className="w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1 border">Platnost</th>
                <th className="px-2 py-1 border">Počet řádků</th>
                <th className="px-2 py-1 border">Zdroj</th>
                <th className="px-2 py-1 border">Uloženo</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 border">
                    {h.validFrom} → {h.validTo}
                  </td>
                  <td className="px-2 py-1 border">{h.rows}</td>
                  <td className="px-2 py-1 border">{h.source}</td>
                  <td className="px-2 py-1 border">{h.savedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-sm text-gray-500">Žádná historie.</div>
        )}
      </div>
    </div>
  );
}
